import React from 'react';

type CertifiedBuildSealProps = {
  status?: string | null;
  /** When true, show muted "In review" for the owner only. */
  showPendingForOwner?: boolean;
  className?: string;
};

/**
 * Stamped metal seal for Bilt:Spec Certified builds.
 * Adult grading-house cue — not a cartoon checkmark.
 */
export const CertifiedBuildSeal: React.FC<CertifiedBuildSealProps> = ({
  status,
  showPendingForOwner = false,
  className = '',
}) => {
  if (status === 'certified') {
    return (
      <div
        className={[
          'inline-flex items-center gap-1 rounded-full',
          'bg-gradient-to-b from-[#141414] to-[#0a0a0a]',
          'border border-amber-500/50',
          'ring-1 ring-inset ring-amber-200/10',
          'px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          'text-amber-200 shadow-lg shadow-black/40',
          className,
        ].join(' ')}
        title="Bilt:Spec Certified: build reviewed by staff"
        aria-label="Bilt:Spec Certified build"
        data-testid="certified-build-seal"
      >
        <svg
          className="h-3 w-3 text-amber-300"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
        >
          <circle cx="12" cy="12" r="9" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.5 12.5l2.2 2.2 4.8-5.2"
          />
        </svg>
        <span>Certified</span>
      </div>
    );
  }

  if (status === 'pending_review' && showPendingForOwner) {
    return (
      <div
        className={[
          'inline-flex items-center gap-1 rounded-full',
          'bg-[#141414]/90 border border-white/[0.10]',
          'px-2 py-0.5 text-[10px] font-medium text-gray-300',
          className,
        ].join(' ')}
        title="Certification request in review"
        aria-label="Certification in review"
        data-testid="certification-pending-chip"
      >
        <span>In review</span>
      </div>
    );
  }

  return null;
};

export default CertifiedBuildSeal;
